import React, {useState, useEffect, useRef, useLayoutEffect} from 'react';
import './Bubbles.scss';
import * as d3 from 'd3';
import {isMobile} from 'react-device-detect';

const RadiusRange = [57, 78, 93];
const FontSizeRange = [24, 28, 32];
const radiusFactor = 20;
const gap = 10;
const paddingX = 40;
const paddingY = 80;

interface BaseLink {
  idx: number;
  title: string;
  href: string;
}

export interface Link extends BaseLink {
  priority: number | null;
}

interface SimulationLink extends BaseLink, d3.SimulationNodeDatum {
  initialX: number;
  initialY: number;
  radius: number;
  fontSize: number;
}

const getPixels = (val: number, fraction = 0) => (val == null ? '' : val.toFixed(fraction) + 'px');
const getTranslate = (x: number, y: number) => `translate3d(${getPixels(x)},${getPixels(y)},0)`;

const getBubbleStyle = (node: SimulationLink, {x, y}, animate: boolean) => ({
  tranistion: animate ? undefined : 'all 10ms',
  transform: animate
    ? getTranslate(node.x - node.radius - x, node.y - node.radius - y)
    : getTranslate(node.initialX - x, node.initialY - y),
  width: getPixels(node.radius * 2),
  height: getPixels(node.radius * 2),
  fontSize: getPixels(node.fontSize),
  // borderWidth: getPixels(node.radius / radiusFactor),
  borderWidth: '3px',
});

type BubblesProps = {
  links: Link[];
  inView: boolean;
  onAnimate(active: boolean): void;
};

const Bubbles = ({links, inView, onAnimate}: BubblesProps) => {
  const ref = useRef<HTMLDivElement>();
  const [nodes, setNodes] = useState<SimulationLink[]>(null);
  const [animate, setAnimate] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState({
    height: 500,
    width: null as number,
    x: 0,
    y: 0,
  });

  useLayoutEffect(() => {
    const diff = ref.current.clientWidth - dimensions.width ?? 0;
    ref.current.scrollLeft = diff / 2;
  }, [dimensions]);

  const onAction = (e: React.SyntheticEvent<HTMLAnchorElement>, node: SimulationLink) => {
    const target = e.currentTarget;
    if (node.href == null || target.classList.contains('clicked')) return;
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    e.nativeEvent.preventDefault();
    const clientMaxDimention = Math.max(document.documentElement.clientWidth, document.documentElement.clientHeight);
    const scale = Math.ceil((1.2 * clientMaxDimention) / node.radius);

    setTimeout(() => {
      target.classList.add('clicked');
      target.style.setProperty('--scale', `${scale}`);
      ref.current.style.overflow = 'visible';
      onAnimate(true);
      setTimeout(() => {
        target?.click();
        setTimeout(() => onAnimate(false), 100);
      }, 1200);
    });
  };

  useEffect(() => {
    if (links == null) return;
    function redraw() {
      const clientWidth = ref.current.clientWidth;
      const clientHeight = 500;
      const strengthX = 0.04;
      const strengthY = 0.05;

      const simulationLinks: SimulationLink[] = links.map((link) => {
        const {title, href, idx} = link;
        const radius = RadiusRange[link.priority ?? 0];
        const fontSize = FontSizeRange[link.priority ?? 0];
        return {
          idx,
          title,
          href,
          radius,
          fontSize,
          initialX: clientWidth / 2 - radius,
          initialY: clientHeight / 2 - radius,
        };
      });

      const d3Simulation = d3
        .forceSimulation(simulationLinks)
        .force('center', d3.forceCenter(clientWidth / 2, clientHeight / 2))
        .force('x', d3.forceX().strength((strengthX * clientHeight) / clientWidth))
        .force('y', d3.forceY().strength((strengthY * clientWidth) / clientHeight))
        .force(
          'collide',
          d3
            .forceCollide<SimulationLink>()
            .strength(0.5)
            .radius((d) => d.radius + gap)
            .iterations(1)
        );

      d3Simulation.stop();
      d3Simulation.tick(100);

      const bounds = simulationLinks.reduce(
        ({maxY, maxX, minY, minX}, d) => ({
          maxY: Math.max(d.y + d.radius, maxY),
          maxX: Math.max(d.x + d.radius, maxX),
          minY: Math.min(d.y - d.radius, minY),
          minX: Math.min(d.x - d.radius, minX),
        }),
        {maxY: -Infinity, maxX: -Infinity, minY: Infinity, minX: Infinity}
      );

      setDimensions({
        height: bounds.maxY - bounds.minY + paddingY * 2,
        width: bounds.maxX - bounds.minX + paddingX * 2,
        y: bounds.minY - paddingY,
        x: bounds.minX - paddingX,
      });

      setNodes(simulationLinks);
    }

    setAnimate(false);
    const timeout = setTimeout(() => setAnimate(true), 10);
    redraw();
    window.addEventListener('resize', redraw);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', redraw);
    };
  }, [links]);

  return (
    <div className='Bubbles' ref={ref} style={{height: getPixels(dimensions.height)}}>
      <div className='Bubbles__container' style={{width: getPixels(dimensions.width)}}>
        {nodes?.map((node) => (
          <a
            className='Bubbles__bubble'
            onClick={(e) => onAction(e, node)}
            onKeyDown={(e) => e.key == 'Enter' && onAction(e, node)}
            key={node.idx}
            href={node.href}
            style={getBubbleStyle(node, dimensions, inView && animate)}>
            <div className='Bubbles__bubble__inner' />
            <div className='Bubbles__bubble__title'>{node.title}</div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default Bubbles;
